import { uniq, uniqBy } from 'lodash';

import makeStringIcon from '../../../../../../components/elementIcon/makeStringIconDef';
import type { ElementIcon } from '../../../../../../components/elementIcon/elementIconTypes';
import {
    filterWords,
    getAcronyms,
    removeSpecialCharacters,
} from '../../../../../../components/elementIcon/elementIconUtils';
import type { IconPickerTab } from './iconPickerTypes';
import { LETTERS_TAB_ID, RECENT_SEARCH_TAB_MAX, RECOMMENDED_TAB_ID, TITLE_WORD_TAB_MAX } from './iconPickerConstants';

/**
 * One form for the request, cache key, recent search and tab label.
 * Legacy desktop and mobile cached by different keys.
 */
export const normaliseIconQuery = (text: string) => text.trim().toLowerCase();

/**
 * Title words and searches share an id, so searching for a title word selects its tab.
 */
export const getSearchTabId = (query: string) => `search:${query}`;

/**
 * Words from a multi-word title worth searching for, skipping filler like "the" and "and".
 */
export const getTitleWords = (title: string): string[] => {
    const words = title
        .split(' ')
        .filter(filterWords())
        .map(removeSpecialCharacters)
        .filter((word) => word.length > 0)
        .map(normaliseIconQuery);

    if (words.length < 2) return [];

    return uniq(words).slice(0, TITLE_WORD_TAB_MAX);
};

/**
 * Recommended, title words, the latest recent searches (oldest first, so new ones land at the bottom), letters.
 */
export const buildIconPickerTabs = (title: string, recentSearches: string[]): IconPickerTab[] => {
    const titleWords = getTitleWords(title);
    const searchQueries = recentSearches.filter((query) => !titleWords.includes(query)).slice(-RECENT_SEARCH_TAB_MAX);

    const searchTabs: IconPickerTab[] = [...titleWords, ...searchQueries].map((query) => ({
        type: 'search',
        id: getSearchTabId(query),
        query,
    }));

    return [{ type: 'recommended', id: RECOMMENDED_TAB_ID }, ...searchTabs, { type: 'letters', id: LETTERS_TAB_ID }];
};

/**
 * Acronyms of the title as string icons, e.g. "IBM", "IB", "I" for "International Business Machines".
 */
export const getTitleLetterIcons = (title: string): ElementIcon[] =>
    getAcronyms(title).map((acronym) => makeStringIcon(acronym));

/**
 * Grid keys must be unique, but a title acronym can repeat a number icon (e.g. "1" in "Top 10 Tips").
 */
export const uniqueIcons = (icons: ElementIcon[]) => uniqBy(icons, 'id');

/**
 * The icon id when every selected board has that icon, so the grid shows it as selected.
 */
export const getSharedIconId = (iconIds: (string | undefined)[]): string | null => {
    const [firstIconId] = iconIds;
    if (!firstIconId) return null;

    const isShared = iconIds.every((iconId) => iconId === firstIconId);
    return isShared ? firstIconId : null;
};
